import { submitCallForm } from '../../../../../Api/ApiMethods';
import { getSelectedProviderId, isNoInsuranceProvider, toNullableBoolFromYesNo } from '../../../../../_helper/mandatoryFieldRules';

const formatDateForFormData = (date) => {
    return date instanceof Date && !isNaN(date) ? date.toISOString() : null;
};

const appendToFormData = (formDataToSend, key, value) => {
    if (typeof value === 'boolean') {
        formDataToSend.append(key, value);
        return;
    }

    formDataToSend.append(key, value === null || value === undefined ? '' : value);
};

const isLorFileType = (workFileTypes, typeId) => {
    if (!workFileTypes || !Array.isArray(workFileTypes)) {
        return false;
    }

    const matchedType = workFileTypes.find((type) => type.id === typeId);
    const title = matchedType?.title ? matchedType.title.toLowerCase() : '';

    return title.includes('hak mahrumiyeti') || title.includes('lor') || title.includes('mahrumiyeti');
};

const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
};

const appendPolicyToFormData = ({
    formDataToSend,
    prefix,
    policy,
    providerProperty,
    providerFormKey,
    numberProperty,
    numberFormKey,
    startProperty,
    startFormKey,
    expiryProperty,
    expiryFormKey,
    insuranceCompanies,
}) => {
    const providerValue = policy?.[providerProperty];
    const providerId = getSelectedProviderId(providerValue);
    const providerList = Array.isArray(providerValue) ? providerValue : (providerId ? [providerId] : []);

    providerList.forEach((id, index) => {
        appendToFormData(formDataToSend, `${prefix}.${providerFormKey}[${index}]`, id);
    });

    const isNoInsurance = isNoInsuranceProvider(providerId, insuranceCompanies);
    const hasProvider = providerList.length > 0;
    const hasAnyDetail =
        hasValue(policy?.[numberProperty]) ||
        hasValue(policy?.[startProperty]) ||
        hasValue(policy?.[expiryProperty]);

    if (!isNoInsurance && (hasProvider || hasAnyDetail)) {
        appendToFormData(formDataToSend, `${prefix}.${numberFormKey}`, policy?.[numberProperty]);
        appendToFormData(formDataToSend, `${prefix}.${startFormKey}`, formatDateForFormData(policy?.[startProperty]));
        appendToFormData(formDataToSend, `${prefix}.${expiryFormKey}`, formatDateForFormData(policy?.[expiryProperty]));
    }
};

export const prepareUpdatedComments = ({ existingComments, currentDepartment, userFullName, notes }) => {
    const updatedComments = [...existingComments];
    const existingCommentIndex = updatedComments.findIndex(
        (comment) => comment.commentType === currentDepartment
    );

    if (notes && notes.trim()) {
        const commentTypeWithUser = `${currentDepartment} - ${userFullName}`;

        if (existingCommentIndex >= 0) {
            updatedComments[existingCommentIndex] = {
                ...updatedComments[existingCommentIndex],
                commentType: commentTypeWithUser,
                comment: notes.trim(),
            };
        } else {
            updatedComments.push({
                commentType: commentTypeWithUser,
                comment: notes.trim(),
            });
        }
    }

    return updatedComments;
};

export const buildCallFormPayload = ({
    formData,
    stateId,
    id,
    isNewFormFromDataPool,
    action,
    additionalParams,
    updatedComments,
    workFileTypes,
    insuranceCompanies,
}) => {
    const formDataToSend = new FormData();

    appendToFormData(formDataToSend, 'CallFormNumber', isNewFormFromDataPool ? '' : id);
    appendToFormData(formDataToSend, 'PoaStatus', formData.poaStatus);
    appendToFormData(formDataToSend, 'CallFormState', stateId);
    appendToFormData(formDataToSend, 'OperationAction', action);

    if (action === 'return' && additionalParams.needExpertReview !== undefined) {
        appendToFormData(formDataToSend, 'NeedExpertReview', additionalParams.needExpertReview);
    }

    const customerData = formData;
    appendToFormData(formDataToSend, 'Customer.CustomerType', customerData.customerType);
    appendToFormData(formDataToSend, 'Customer.ClientName', customerData.clientName);
    appendToFormData(formDataToSend, 'Customer.CompanyName', customerData.companyName);
    appendToFormData(formDataToSend, 'Customer.IdentityNumber', customerData.tcid);
    appendToFormData(formDataToSend, 'Customer.TaxNumber', customerData.taxNumber);
    appendToFormData(formDataToSend, 'Customer.CrsNumber', customerData.crsNumber);
    appendToFormData(formDataToSend, 'Customer.ContractRate', customerData.customerContractRate);
    appendToFormData(formDataToSend, 'Customer.EnforcementAttorneyFeeShareRate', customerData.customerEnforcementAttorneyFeeShareRate);
    appendToFormData(formDataToSend, 'Customer.JudgmentAttorneyFeeShareRate', customerData.customerJudgmentAttorneyFeeShareRate);
    appendToFormData(formDataToSend, 'Customer.PayInterestToCustomer', customerData.customerPayInterestToCustomer);
    appendToFormData(formDataToSend, 'Customer.CompanyRepresentative', customerData.companyRepresentative);
    appendToFormData(formDataToSend, 'Customer.Middleman', customerData.middleman);
    appendToFormData(formDataToSend, 'Customer.MiddlemanIban', customerData.middlemanIban);
    appendToFormData(formDataToSend, 'Customer.IsMiddlemanContractBased', customerData.customerIsMiddlemanContractBased);
    appendToFormData(formDataToSend, 'Customer.MiddlemanContractRate', customerData.customerMiddlemanContractRate);
    appendToFormData(formDataToSend, 'Customer.MiddlemanContractPrice', customerData.customerMiddlemanContractPrice);
    appendToFormData(formDataToSend, 'Customer.ContractOriginalArrived', toNullableBoolFromYesNo(customerData.hasContractArrived));
    appendToFormData(formDataToSend, 'Customer.Address', customerData.address);
    appendToFormData(formDataToSend, 'Customer.Reference', customerData.reference);

    if (customerData.agentId) appendToFormData(formDataToSend, 'Customer.Agent[0]', customerData.agentId);
    if (customerData.district) appendToFormData(formDataToSend, 'Customer.District[0]', customerData.district);

    const emails = [
        ...(customerData.email ? [{ EmailDescription: customerData.emailDescription || 'Ana E-posta', Email: customerData.email }] : []),
        ...(customerData.email2 ? [{ EmailDescription: customerData.emailDescription2 || 'İkinci E-posta', Email: customerData.email2 }] : []),
    ];
    emails.forEach((email, index) => {
        appendToFormData(formDataToSend, `Customer.Emails[${index}].EmailDescription`, email.EmailDescription);
        appendToFormData(formDataToSend, `Customer.Emails[${index}].Email`, email.Email);
    });

    const ibans = [
        ...(customerData.iban ? [{ IbanDescription: customerData.ibanDescription || 'Ana Hesap', Iban: customerData.iban }] : []),
        ...(customerData.iban2 ? [{ IbanDescription: formData.ibanDescription2 || 'İkinci Hesap', Iban: formData.iban2 }] : []),
    ];
    ibans.forEach((iban, index) => {
        appendToFormData(formDataToSend, `Customer.Ibans[${index}].IbanDescription`, iban.IbanDescription);
        appendToFormData(formDataToSend, `Customer.Ibans[${index}].Iban`, iban.Iban);
    });

    const phones = [
        ...(customerData.phoneNumber ? [{ PhoneDescription: customerData.phoneDescription || 'Ana Telefon', Phone: customerData.phoneNumber }] : []),
        ...(customerData.phoneNumber2 ? [{ PhoneDescription: customerData.phoneDescription2 || 'İkinci Telefon', Phone: customerData.phoneNumber2 }] : []),
    ];
    phones.forEach((phone, index) => {
        appendToFormData(formDataToSend, `Customer.Phones[${index}].PhoneDescription`, phone.PhoneDescription);
        appendToFormData(formDataToSend, `Customer.Phones[${index}].Phone`, phone.Phone);
    });

    const plateData = formData;
    appendToFormData(formDataToSend, 'LicensePlate.LicensePlateNumber', plateData.licensePlate);
    appendToFormData(formDataToSend, 'LicensePlate.CarchassisNumber', plateData.chassisNumber);
    appendToFormData(formDataToSend, 'LicensePlate.IsThereLovClaim', plateData.isThereLovClaim === 'yes');
    if (plateData.isThereLovClaim === 'yes') {
        appendToFormData(formDataToSend, 'LicensePlate.LovClaimAmount', plateData.lovClaimAmount);
    }
    appendToFormData(formDataToSend, 'LicensePlate.LpImpeachmentStatus', plateData.lpImpeachmentStatus === 'yes');
    if (plateData.lpImpeachmentStatus === 'yes' || plateData.lpImpeachmentStatus === true) {
        appendToFormData(formDataToSend, 'LicensePlate.LpImpeachmentAmount', plateData.lpImpeachmentAmount);
    }

    appendToFormData(formDataToSend, 'LicensePlate.HasCarSoldBefore', plateData.hasCarSoldBefore === 'yes');
    appendToFormData(formDataToSend, 'LicensePlate.CarBrand', plateData.carBrand);
    appendToFormData(formDataToSend, 'LicensePlate.CarModel', plateData.carModel);
    appendToFormData(formDataToSend, 'LicensePlate.CarYear', plateData.carYear);
    appendToFormData(formDataToSend, 'LicensePlate.CarOwner', plateData.clientName || plateData.companyName);
    appendToFormData(formDataToSend, 'LicensePlate.Description', null);
    appendToFormData(formDataToSend, 'LicensePlate.CustomerContractRate', plateData.licensePlateCustomerContractRate);
    appendToFormData(formDataToSend, 'LicensePlate.EnforcementAttorneyFeeShareRate', plateData.licensePlateEnforcementAttorneyFeeShareRate);
    appendToFormData(formDataToSend, 'LicensePlate.JudgmentAttorneyFeeShareRate', plateData.licensePlateJudgmentAttorneyFeeShareRate);
    appendToFormData(formDataToSend, 'LicensePlate.PayInterestToCustomer', plateData.licensePlatePayInterestToCustomer);
    appendToFormData(formDataToSend, 'LicensePlate.IsMiddlemanContractBased', plateData.licensePlateIsMiddlemanContractBased);
    appendToFormData(formDataToSend, 'LicensePlate.MiddlemanContractRate', plateData.licensePlateMiddlemanContractRate);
    appendToFormData(formDataToSend, 'LicensePlate.MiddlemanContractPrice', plateData.licensePlateMiddlemanContractPrice);

    appendPolicyToFormData({
        formDataToSend,
        prefix: 'LicensePlate.TrafficInsurancePolicy',
        policy: plateData.trafficInsurancePolicy,
        providerProperty: 'trafficInsuranceProvider',
        providerFormKey: 'TrafficInsuranceProvider',
        numberProperty: 'trafficInsuranceNumber',
        numberFormKey: 'TrafficInsuranceNumber',
        startProperty: 'trafficInsuranceStart',
        startFormKey: 'TrafficInsuranceStart',
        expiryProperty: 'trafficInsuranceExpiry',
        expiryFormKey: 'TrafficInsuranceExpiry',
        insuranceCompanies,
    });

    appendPolicyToFormData({
        formDataToSend,
        prefix: 'LicensePlate.CascoInsurancePolicy',
        policy: plateData.cascoInsurancePolicy,
        providerProperty: 'cascoInsuranceProvider',
        providerFormKey: 'CascoInsuranceProvider',
        numberProperty: 'cascoInsuranceNumber',
        numberFormKey: 'CascoInsuranceNumber',
        startProperty: 'cascoInsuranceStart',
        startFormKey: 'CascoInsuranceStart',
        expiryProperty: 'cascoInsuranceExpiry',
        expiryFormKey: 'CascoInsuranceExpiry',
        insuranceCompanies,
    });
    appendToFormData(formDataToSend, 'LicensePlate.IsThereOpenDamageFile', toNullableBoolFromYesNo(plateData.isThereOpenDamageFile));

    const poaData = formData;
    appendToFormData(formDataToSend, 'PowerOfAttorney.PoaNumber', poaData.powerOfAttorneyNumber);
    appendToFormData(formDataToSend, 'PowerOfAttorney.PoaDate', formatDateForFormData(poaData.powerOfAttorneyDate));
    appendToFormData(formDataToSend, 'PowerOfAttorney.IsPoaTimeLimited', toNullableBoolFromYesNo(poaData.isPowerOfAttorneyTimed));
    appendToFormData(formDataToSend, 'PowerOfAttorney.PoaEndDate', poaData.isPowerOfAttorneyTimed === 'yes' ? formatDateForFormData(poaData.powerOfAttorneyEndDate) : null);
    appendToFormData(formDataToSend, 'PowerOfAttorney.NotaryOfPoa', poaData.notaryNumber);
    appendToFormData(formDataToSend, 'PowerOfAttorney.IsLicensePlatesLimited', toNullableBoolFromYesNo(poaData.isLicensePlatesLimited));
    appendToFormData(formDataToSend, 'PowerOfAttorney.IsAccidentDateLimited', toNullableBoolFromYesNo(poaData.isAccidentDateLimited));
    appendToFormData(formDataToSend, 'PowerOfAttorney.LicensePlateLimitAccidentDate', poaData.isLicensePlatesLimited === 'yes' && poaData.isAccidentDateLimited === 'yes' ? formatDateForFormData(poaData.plateLimitAccidentDate) : null);
    appendToFormData(formDataToSend, 'PowerOfAttorney.PoaOriginalArrived', toNullableBoolFromYesNo(poaData.hasOriginalArrived));
    appendToFormData(formDataToSend, 'PowerOfAttorney.ContractRate', poaData.contractRate);

    if (poaData.isLicensePlatesLimited === 'yes' && Array.isArray(poaData.plateLimit)) {
        poaData.plateLimit.forEach((plateId, index) => {
            appendToFormData(formDataToSend, `PowerOfAttorney.LicensePlateLimit[${index}]`, plateId);
        });
    }

    const accidentData = formData;
    appendToFormData(formDataToSend, 'Accident.AccidentDate', formatDateForFormData(accidentData.accidentDate));
    appendToFormData(formDataToSend, 'Accident.CounterpartyType', accidentData.counterpartyType);
    appendToFormData(formDataToSend, 'Accident.CounterpartyIdentificationNumber', accidentData.counterpartyIdentificationNumber);
    appendToFormData(formDataToSend, 'Accident.CounterpartyTaxNumber', accidentData.counterpartyTaxNumber);
    appendToFormData(formDataToSend, 'Accident.CounterpartyCarOwner', accidentData.counterpartyCarOwner);
    appendToFormData(formDataToSend, 'Accident.CounterpartyLicensePlateNumber', accidentData.counterpartyLicensePlateNumber);
    appendToFormData(formDataToSend, 'Accident.CounterpartyAddress', accidentData.counterpartyAddress);
    appendToFormData(formDataToSend, 'Accident.IsTrialDocument', accidentData.isTrialDocument === 'yes');
    appendToFormData(formDataToSend, 'Accident.IsWorkDocument', accidentData.isWorkDocument === 'yes');
    appendToFormData(formDataToSend, 'Accident.BonusEligibility', accidentData.bonusEligibility);
    appendToFormData(formDataToSend, 'Accident.ShouldPayNotaryFee', accidentData.shouldPayNotaryFee === 'yes');
    appendToFormData(formDataToSend, 'Accident.NotaryFeeAmount', accidentData.shouldPayNotaryFee === 'yes' ? accidentData.notaryFeeAmount : '');
    appendToFormData(formDataToSend, 'Accident.EstimatedDepreciationAmount', accidentData.estimatedDepreciationAmount);
    appendToFormData(formDataToSend, 'Accident.DamageAmount', accidentData.damageAmount);
    appendToFormData(formDataToSend, 'Accident.DamageDifferenceAmount', accidentData.damageDifferenceAmount);
    appendToFormData(formDataToSend, 'Accident.LoRamount', accidentData.lorAmount);
    appendToFormData(formDataToSend, 'Accident.OurFaultRate', accidentData.ourFaultRate);
    appendToFormData(formDataToSend, 'Accident.CounterPartyFaultRate', accidentData.counterpartyFaultRate);
    appendToFormData(formDataToSend, 'Accident.WillLorBeWorked', accidentData.willLorBeWorked === 'yes');
    appendToFormData(formDataToSend, 'Accident.DocumentRequestDate', formatDateForFormData(accidentData.documentRequestDate));
    appendToFormData(formDataToSend, 'Accident.DocumentReceivedDate', formatDateForFormData(accidentData.documentReceivedDate));

    appendPolicyToFormData({
        formDataToSend,
        prefix: 'Accident.CounterpartyTrafficInsurancePolicy',
        policy: accidentData.counterpartyTrafficInsurancePolicy,
        providerProperty: 'trafficInsuranceProvider',
        providerFormKey: 'TrafficInsuranceProvider',
        numberProperty: 'trafficInsuranceNumber',
        numberFormKey: 'TrafficInsuranceNumber',
        startProperty: 'trafficInsuranceStart',
        startFormKey: 'TrafficInsuranceStart',
        expiryProperty: 'trafficInsuranceExpiry',
        expiryFormKey: 'TrafficInsuranceExpiry',
        insuranceCompanies,
    });

    appendPolicyToFormData({
        formDataToSend,
        prefix: 'Accident.CounterpartyCascoInsurancePolicy',
        policy: accidentData.counterpartyCascoInsurancePolicy,
        providerProperty: 'cascoInsuranceProvider',
        providerFormKey: 'CascoInsuranceProvider',
        numberProperty: 'cascoInsuranceNumber',
        numberFormKey: 'CascoInsuranceNumber',
        startProperty: 'cascoInsuranceStart',
        startFormKey: 'CascoInsuranceStart',
        expiryProperty: 'cascoInsuranceExpiry',
        expiryFormKey: 'CascoInsuranceExpiry',
        insuranceCompanies,
    });

    const accidentFileData = formData;
    appendToFormData(formDataToSend, 'AccidentFile.FileNumber', accidentFileData.fileNumber);
    appendToFormData(formDataToSend, 'AccidentFile.FileOpenDate', formatDateForFormData(accidentFileData.registrationDate));
    appendToFormData(formDataToSend, 'AccidentFile.FileStatus', accidentFileData.fileStatus);
    appendToFormData(formDataToSend, 'AccidentFile.WhoseInsurance', accidentFileData.whoseInsurance);
    appendToFormData(formDataToSend, 'AccidentFile.DocumentRequestStatus', accidentFileData.documentRequestStatus);
    appendToFormData(formDataToSend, 'AccidentFile.ImpeachmentStatus', '');

    if (accidentFileData.customerId) appendToFormData(formDataToSend, 'AccidentFile.Customer[0]', accidentFileData.customerId);
    if (accidentFileData.accidentId) appendToFormData(formDataToSend, 'AccidentFile.Accident.Id', accidentFileData.accidentId);

    if (accidentFileData.jobToBeWorked && Array.isArray(accidentFileData.jobToBeWorked)) {
        accidentFileData.jobToBeWorked.forEach((jobId, index) => {
            appendToFormData(formDataToSend, `AccidentFile.JobToBeWorked[${index}]`, jobId);
        });
    } else if (accidentFileData.jobToBeWorked) {
        appendToFormData(formDataToSend, 'AccidentFile.JobToBeWorked[0]', accidentFileData.jobToBeWorked);
    }

    accidentFileData.filesToBeWorked.forEach((ftw, accidentFileIndex) => {
        appendToFormData(formDataToSend, `MultipleAccidentFile[${accidentFileIndex}].WithReport`, ftw.withReport);
        appendToFormData(formDataToSend, `MultipleAccidentFile[${accidentFileIndex}].WithFault`, ftw.withFault);
        appendToFormData(formDataToSend, `MultipleAccidentFile[${accidentFileIndex}].WithoutReport`, ftw.withoutReport);

        if (ftw.fileType && Array.isArray(ftw.fileType)) {
            ftw.fileType.forEach((typeId, fileTypeIndex) => {
                const ftwPrefix = `MultipleAccidentFile[${accidentFileIndex}].FileTypeAndExpense[${fileTypeIndex}]`;
                appendToFormData(formDataToSend, `${ftwPrefix}.FileType`, typeId);

                let fileExpense = 0;

                if (ftw.fileTypeExpenseDetails && ftw.fileTypeExpenseDetails[typeId] !== undefined) {
                    fileExpense = ftw.fileTypeExpenseDetails[typeId];

                    if (isLorFileType(workFileTypes, typeId)) {
                        appendToFormData(formDataToSend, `${ftwPrefix}.NumberOfDays`, ftw.numberOfDays || 0);
                        appendToFormData(formDataToSend, `${ftwPrefix}.ExpensePerDay`, ftw.expensePerDay || 0);
                    }
                } else if (isLorFileType(workFileTypes, typeId)) {
                    appendToFormData(formDataToSend, `${ftwPrefix}.NumberOfDays`, ftw.numberOfDays || 0);
                    appendToFormData(formDataToSend, `${ftwPrefix}.ExpensePerDay`, ftw.expensePerDay || 0);
                    fileExpense = ftw.lorExpense || (ftw.numberOfDays * ftw.expensePerDay) || 0;
                } else {
                    fileExpense = ftw.fileExpense || 0;
                }

                appendToFormData(formDataToSend, `${ftwPrefix}.FileExpense`, fileExpense);
            });
        }
    });

    updatedComments.forEach((comment, index) => {
        appendToFormData(formDataToSend, `Comments[${index}].CommentType`, comment.commentType);
        appendToFormData(formDataToSend, `Comments[${index}].Comment`, comment.comment);
    });

    return formDataToSend;
};

export const submitCallFormRequest = async ({ id, isNewFormFromDataPool, formDataToSend }) => {
    return submitCallForm({
        id,
        isNewFormFromDataPool,
        formData: formDataToSend,
    });
};
